import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Home, ArrowLeft, MapPin, Users, BarChart3, Workflow, HelpCircle, Mail, FileText, Play } from 'lucide-react';
import { useBranding } from '../contexts/BrandingContext';
import { useCRM } from '../contexts/CRMContext';

const NotFound = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get branding context - will use defaults if not available
  const brandingContext = useBranding();
  const branding = brandingContext?.branding || null;
  
  // Prefer CRM context for auth status; fallback to localStorage check
  // Note: NotFound is rendered within CRMProvider, so useCRM should be available
  const crmContext = useCRM();
  
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check authentication status
  useEffect(() => {
    // Prefer context; fallback to localStorage check
    if (crmContext?.isAuthenticated !== undefined) {
      setIsAuthenticated(!!crmContext.isAuthenticated);
    } else {
      setIsAuthenticated(!!localStorage.getItem('crm_token'));
    }
  }, [crmContext?.isAuthenticated]);

  useEffect(() => {
    // Title + robots (avoid indexing 404s)
    document.title = 'Page not found – LiveLoveSpain CRM';
    
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement('meta');
      robots.setAttribute('name', 'robots');
      document.head.appendChild(robots);
    }
    robots.setAttribute('content', 'noindex, nofollow');

    // Analytics (guarded, privacy-safe)
    if (window.gtag) {
      window.gtag('event', '404_page_view', {
        page_path: location.pathname,
        referrer: document.referrer || 'direct',
      });
    }

    // Send to monitoring endpoint instead of console (commented out - implement when endpoint is ready)
    // fetch('/api/telemetry/404', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify({
    //     path: location.pathname,
    //     referrer: document.referrer || null,
    //     ts: Date.now()
    //   })
    // }).catch(() => {});
  }, [location.pathname]);

  const primaryColor = branding?.primaryColor || '#3B82F6';
  const secondaryColor = branding?.secondaryColor || '#10B981';

  // Quick links for authenticated users
  const quickLinks = [
    { path: '/clients', label: 'Clients', icon: Users },
    { path: '/workflows', label: 'Workflows', icon: Workflow },
    { path: '/analytics', label: 'Analytics', icon: BarChart3 },
    { path: '/dashboard', label: 'Dashboard', icon: Home }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      {/* Subtle header with logo */}
      <header className="w-full px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        <div className="max-w-7xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center gap-2 hover:opacity-80 transition-opacity"
            aria-label="Go to LiveLoveSpain homepage"
          >
            <img 
              src="/LiveLoveSpain.png" 
              alt="LiveLoveSpain" 
              width={120} 
              height={24} 
              className="h-6 sm:h-7 w-auto" 
            />
            <span className="sr-only">LiveLoveSpain</span>
          </Link>
        </div>
      </header>

      {/* Main content - centered */}
      <div className="flex-1 flex items-center justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-2xl w-full">
          <div className="text-center w-full">
            {/* Relocation-themed illustration */}
            <div
              className="mx-auto mb-8 relative"
              role="img"
              aria-label="Lost location marker illustration"
            >
              <div className="inline-flex items-center justify-center w-32 h-32 rounded-full bg-blue-50 dark:bg-blue-950/20 mb-4">
                <MapPin className="h-16 w-16 text-blue-500 dark:text-blue-400" strokeWidth={1.5} aria-hidden="true" />
              </div>
              <div className="absolute -top-2 -right-2 text-6xl font-bold text-gray-200 dark:text-gray-700" aria-hidden="true">
                404
              </div>
          </div>

            {/* Headline */}
            <h1 className="mt-4 text-4xl font-bold text-gray-900 dark:text-gray-100">
              Oops — looks like the relocation ended here.
            </h1>
            
            {/* Subtext */}
            <p className="mt-4 text-lg text-gray-600 dark:text-gray-400">
              We couldn't find that page. Let's get you back on track to moving clients, not missing pages.
            </p>
            
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-500">
              The link may be broken or the page has moved. Sorry about that.
          </p>
        </div>
        
        {/* Primary CTAs */}
        <div className="mt-8 flex flex-col sm:flex-row justify-center items-center gap-2.5 sm:gap-3 max-w-md sm:max-w-none mx-auto sm:mx-0">
          {isAuthenticated ? (
            <Link
              to="/dashboard"
              className="w-full sm:w-auto inline-flex items-center justify-center px-4 sm:px-5 py-2 sm:py-2.5 rounded-lg shadow-sm text-sm font-semibold text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 transform hover:scale-105"
              style={{ backgroundColor: primaryColor }}
              aria-label="Go to my dashboard"
            >
              <Home className="h-4 w-4 mr-2" aria-hidden="true" />
              Go to My Dashboard
            </Link>
          ) : (
            <>
              <Link
                to="/"
                className="w-full sm:w-auto inline-flex items-center justify-center px-4 sm:px-5 py-2 sm:py-2.5 rounded-lg shadow-sm text-sm font-semibold text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 transform hover:scale-105"
                style={{ backgroundColor: primaryColor }}
                aria-label="Go to homepage"
              >
                <Home className="h-4 w-4 mr-2" aria-hidden="true" />
                Go to Homepage
              </Link>
              {/* High-intent actions for unauthenticated users */}
              <Link
                to="/signup"
                className="w-full sm:w-auto inline-flex items-center justify-center px-4 sm:px-5 py-2 sm:py-2.5 rounded-lg border-2 bg-white dark:bg-gray-800 text-sm font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200"
                style={{ borderColor: primaryColor, color: primaryColor }}
                aria-label="Start free trial"
              >
                Start Free Trial
              </Link>
          <Link
                to="/#demo"
                className="w-full sm:w-auto inline-flex items-center justify-center px-4 sm:px-5 py-2 sm:py-2.5 rounded-lg border-2 bg-white dark:bg-gray-800 text-sm font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200"
                style={{ borderColor: secondaryColor, color: secondaryColor }}
                aria-label="Watch CRM demo"
          >
                <Play className="h-4 w-4 mr-2" aria-hidden="true" />
                Watch Demo
          </Link>
            </>
          )}
          
          <button
            onClick={() => navigate(-1)}
            className="w-full sm:w-auto inline-flex items-center justify-center px-4 sm:px-5 py-2 sm:py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200"
            aria-label="Go back to previous page"
          >
            <ArrowLeft className="h-4 w-4 mr-2" aria-hidden="true" />
            Go Back
          </button>
        </div>

        {/* Quick links for signed-in users */}
        {isAuthenticated && (
          <div className="mt-12">
            <h3 className="text-center text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-4">
              Quick Navigation
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-2xl mx-auto">
              {quickLinks.map((link) => {
                const Icon = link.icon;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="flex flex-col items-center justify-center p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-blue-300 dark:hover:border-blue-600 hover:shadow-md transition-all duration-200 group"
                    aria-label={`Go to ${link.label}`}
                  >
                    <Icon className="h-6 w-6 mb-2 text-gray-400 dark:text-gray-500 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors" aria-hidden="true" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                      {link.label}
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Support */}
        <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row justify-center items-center gap-6 text-sm">
            <Link
              to="/contact"
              className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              aria-label="Open Help Center"
            >
              <HelpCircle className="h-4 w-4 mr-2" aria-hidden="true" />
              Help Center
            </Link>
            <a
              href="mailto:info@livelovespain.com"
              className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              aria-label="Contact Support"
            >
              <Mail className="h-4 w-4 mr-2" aria-hidden="true" />
              Contact Support
            </a>
            {!isAuthenticated && (
              <Link
                to="/#demo"
                className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                aria-label="See CRM demo"
              >
                <FileText className="h-4 w-4 mr-2" aria-hidden="true" />
                See CRM Demo
              </Link>
            )}
          </div>
        </div>

        {/* Report broken link (subtle) */}
        <div className="mt-8 text-center">
          <button
            onClick={() => {
              // Prefer internal endpoint; fall back to mailto
              // fetch('/api/report-404', {
              //   method: 'POST',
              //   headers: { 'Content-Type': 'application/json' },
              //   body: JSON.stringify({
              //     url: window.location.href,
              //     referrer: document.referrer || null
              //   })
              // }).catch(() => {});
              
              const subject = encodeURIComponent('Broken Link Report');
              const body = encodeURIComponent(`I encountered a 404 on: ${window.location.href}\nReferrer: ${document.referrer || 'Direct'}\n\nWhat I expected to find: `);
              window.location.href = `mailto:info@livelovespain.com?subject=${subject}&body=${body}`;
            }}
            className="text-xs text-gray-400 dark:text-gray-600 hover:text-gray-600 dark:hover:text-gray-400 underline transition-colors"
            aria-label="Report this broken link"
          >
            Report this broken link
          </button>
        </div>
        </div>
      </div>
    </div>
  );
};

export default NotFound;